<template>
  <div class="mt-n5">
    <v-card
      v-if="isLoading"
      class="pa-2"
    >
      <v-skeleton-loader
        ref="skeleton"
        :boilerplate="false"
        :type="'list-item-avatar, article, list-item-three-line@6'"
        class="mx-auto"
        width="100vw"
        max-width="600"
      />
    </v-card>

    <v-card
      v-if="coach && !isLoading"
      class="mx-auto"
      width="100vw"
      max-width="600"
      :loading="isSaving"
    >
      <v-card-title>
        <span class="primary--text">
          Coach profile
        </span>
        <v-spacer />
        <v-btn
          v-if="!editFlag"
          color="primary lighten-2"
          @click="editFlag = true"
        >
          Edit
          <v-icon
            small
          >
            create
          </v-icon>
        </v-btn>
        <v-btn
          v-else
          :loading="isSaving"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-title>
      <v-card-text class="text--primary">
        <v-form
          ref="form"
          v-model="valid"
        >
          <v-row>
            <v-col
              cols="12"
              sm="8"
              order="2"
              order-sm="1"
            >
              <v-text-field
                v-model="coach.first_name"
                :rules="apiRules.first_name || rules.name"
                :disabled="!editFlag"
                label="First Name"
                required
                @keyup="apiRuleClear('first_name')"
              />

              <v-text-field
                v-model="coach.last_name"
                :rules="apiRules.last_name || rules.name"
                :disabled="!editFlag"
                label="Last Name"
                required
                @keyup="apiRuleClear('last_name')"
              />

              <!--<v-text-field
                v-model="coach.email"
                :rules="rules.email"
                label="E-mail"
                :disabled="true"
              />-->

              <!--<v-combobox
                v-model="selectedTimezone"
                label="Time zone"
                :rules="rules.timezone"
                required
                :disabled="!editFlag"
                :items="timezoneList"
                item-text="display"
                return-object
              />-->

              <!--<v-btn
                class="mb-2"
                outlined
                :disabled="!editFlag"
                @click="passwordDialog = true"
              >
                Change Password
              </v-btn>-->
            </v-col>

            <v-col
              cols="12"
              sm="4"
              order="1"
              order-sm="2"
              align-self="center"
              class="d-flex justify-center"
            >
              <v-avatar
                size="130"
                :color="!coach.profile_image ? 'primary lighten-1' : ''"
              >
                <v-img
                  v-if="coach.profile_image"
                  :src="coach.profile_image"
                  :lazy-src="coach.profile_image"
                  alt="Profile"
                >
                  <template #placeholder>
                    <v-row
                      class="fill-height ma-0"
                      align="center"
                      justify="center"
                    >
                      <v-progress-circular
                        indeterminate
                        color="grey lighten-5"
                      />
                    </v-row>
                  </template>
                </v-img>

                <span
                  v-else
                  class="white--text display-1"
                >
                  {{ initials }}
                </span>
              </v-avatar>

              <v-btn
                v-if="editFlag"
                color="primary"
                fab
                x-small
                dark
                class="align-self-end ml-n5"
                @click="imageDialog = true"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <div class="mb-2">
            <Locations
              :edit-flag="editFlag"
              :locations.sync="locations"
              @location-save="locationSave"
            />

            <div class="d-flex justify-space-between align-end mb-2 mt-5">
              <v-label>Company</v-label>
            </div>

            <v-expansion-panels
              focusable
            >
              <v-expansion-panel>
                <v-expansion-panel-header>Company information</v-expansion-panel-header>
                <v-expansion-panel-content
                  v-if="coach.profile.company"
                >
                  <v-text-field
                    v-model="coach.profile.company.name"
                    label="Name"
                    :disabled="!editFlag"
                    :rules="(apiRules.profile && apiRules.profile.company
                      && apiRules.profile.company.name)"
                    @change="apiRuleClear('profile.company.name')"
                  />
                  <v-text-field
                    v-model="coach.profile.company.email"
                    label="Contact Email"
                    :rules="(apiRules.profile && apiRules.profile.company
                      && apiRules.profile.company.email) || rules.validEmail"
                    :disabled="!editFlag"
                    @change="apiRuleClear('profile.company.email')"
                  />
                  <v-text-field
                    v-model="coach.profile.company.telephone"
                    label="Contact Telephone"
                    :disabled="!editFlag"
                    :rules="(apiRules.profile && apiRules.profile.company
                      && apiRules.profile.company.telephone)"
                    @change="apiRuleClear('profile.company.telephone')"
                  />
                  <v-text-field
                    v-model="coach.profile.company.website"
                    label="Website"
                    :rules="(apiRules.profile && apiRules.profile.company
                      && apiRules.profile.company.website) || rules.validLink"
                    :disabled="!editFlag"
                    @change="apiRuleClear('profile.company.website')"
                  />
                  <v-textarea
                    v-model="coach.profile.company.address"
                    label="Address"
                    rows="4"
                    outlined
                    :disabled="!editFlag"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-divider
              class="mt-6 mb-5"
            />

            <div style="display: flex; align-items: center;">
              <v-text-field
                v-model="coach.profile.url_index"
                placeholder="Profile URL"
                readonly
                :prefix="prefixUrl"
                :disabled="!editFlag"
                required
              >
                <template #append>
                  <v-icon
                    v-if="coach.profile.url_index"
                    @click="copyLinkToClipboard('profile')"
                  >
                    mdi-content-copy
                  </v-icon>
                </template>
              </v-text-field>
              <v-btn
                class="ml-2"
                small
                outlined
                :disabled="!editFlag"
                @click="urlFlag = true"
              >
                Edit URL
              </v-btn>
            </div>
          </div>

          <v-text-field
            v-model="coach.profile.title"
            label="Title"
            :disabled="!editFlag"
            :rules="(apiRules.profile && apiRules.profile.title)"
            @change="apiRuleClear('profile.title')"
          />

          <v-text-field
            v-model="coach.profile.linkedin_url"
            label="LinkedIn Link"
            :append-outer-icon="coach.profile.linkedin_url ? 'mdi-content-copy' : ''"
            :disabled="!editFlag"
            :rules="(apiRules.profile && apiRules.profile.linkedin_url) || rules.validLink"
            @click:append-outer="copyLinkToClipboard('linkedin')"
            @change="apiRuleClear('profile.linkedin_url')"
          />

          <v-autocomplete
            v-model="coach.profile.exp_years"
            :items="experiences"
            class="mb-3"
            item-text="label"
            item-value="key"
            label="Experience years"
            hint="How many years of coaching experience do you have?"
            persistent-hint
            :disabled="!editFlag"
          />

          <v-autocomplete
            v-model="coach.profile.languages"
            :items="languages"
            class="mb-3"
            item-text="value"
            return-object
            label="Languages"
            multiple
            chips
            :deletable-chips="true"
            :disabled="!editFlag"
            hint="What are the languages you can use professionally?"
            persistent-hint
          />

          <v-label>Bio</v-label>
          <div
            :style="{
              backgroundColor: editFlag ? 'transparent' : 'rgba(0, 0, 0, 0.1)',
              color: editFlag ? '' : '#c4c4c4',
              pointerEvents: !editFlag ? 'none' : 'auto',
            }"
          >
            <vue-editor
              id="professionalBioEditor"
              v-model="coach.profile.bio"
              :editor-toolbar="editorToolbar"
              class="mb-4"
            />
          </div>

          <v-label>Experience</v-label>
          <div
            :style="{
              backgroundColor: editFlag ? 'transparent' : 'rgba(0, 0, 0, 0.1)',
              color: editFlag ? '' : '#c4c4c4',
              pointerEvents: !editFlag ? 'none' : 'auto',
            }"
          >
            <vue-editor
              id="professionalExpEditor"
              v-model="coach.profile.experience"
              :editor-toolbar="editorToolbar"
              class="mb-4"
            />
          </div>

          <v-label>Qualifications</v-label>
          <div
            :style="{
              backgroundColor: editFlag ? 'transparent' : 'rgba(0, 0, 0, 0.1)',
              color: editFlag ? '' : '#c4c4c4',
              pointerEvents: !editFlag ? 'none' : 'auto',
            }"
          >
            <vue-editor
              id="professionalQualificationsEditor"
              v-model="coach.profile.qualifications"
              :editor-toolbar="editorToolbar"
              class="mb-4"
            />
          </div>

          <div class="mb-2">
            <v-label>Accreditations</v-label>

            <v-container class="pa-0">
              <v-row
                align="center"
                justify="start"
              >
                <span
                  v-for="(acc, key) in coach.profile.accreditations"
                  :key="key"
                  class="d-flex pr-0 pb-0 flex-wrap"
                >
                  <v-chip
                    class="accreditationChip"
                    close
                    :disabled="!editFlag"
                    @click:close="removeAccreditation(key)"
                  >
                    {{ acc }}
                  </v-chip>
                </span>

                <v-col
                  cols="12"
                >
                  <v-text-field
                    v-model="accreditationsInput"
                    hide-details
                    placeholder="Accreditation"
                    clearable
                    counter="255"
                    maxlength="255"
                    :disabled="!editFlag"
                    class="pt-0"
                    @keydown.enter="addAccreditation"
                  >
                    <template #append-outer>
                      <v-btn
                        color="primary lighten-2"
                        small
                        :disabled="!editFlag"
                        @click="addAccreditation"
                      >
                        Add
                      </v-btn>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </div>

          <div class="d-flex justify-space-between align-end mb-2">
            Testimonials
          </div>

          <template v-if="coach && coach.profile && coach.profile.testimonials">
            <v-card
              v-for="(testimonial, key) in coach.profile.testimonials"
              :key="key"
              class="mb-3"
            >
              <v-card-title class="justify-space-between pb-0">
                <span class="body-1 font-weight-bold">
                  Testimonial {{ key + 1 }}
                  {{ testimonial.author ? `- ${testimonial.author}` : '' }}
                </span>

                <v-btn
                  color="error"
                  text
                  :disabled="!editFlag"
                  @click="removeTestimonial(key)"
                >
                  Remove
                </v-btn>
              </v-card-title>
              <v-card-text>
                <!--<v-input v-model="testimonial.author" />-->
                <v-text-field
                  v-model="testimonial.author"
                  label="Author"
                  :rules="rules.testimonial"
                  required
                  :disabled="!editFlag"
                />

                <v-text-field
                  v-model="testimonial.position"
                  label="Position"
                  :rules="rules.testimonial"
                  required
                  :disabled="!editFlag"
                />

                <v-text-field
                  v-model="testimonial.company"
                  label="Company Name"
                  :rules="rules.testimonial"
                  required
                  :disabled="!editFlag"
                />

                <div
                  :style="{
                    backgroundColor: editFlag ? 'transparent' : 'rgba(0, 0, 0, 0.1)',
                    color: editFlag ? '' : '#c4c4c4',
                    pointerEvents: !editFlag ? 'none' : 'auto',
                  }"
                >
                  <vue-editor
                    v-model="testimonial.content"
                    :editor-toolbar="editorToolbar"
                    class="mb-4"
                    :class="testimonial.flag ? 'EditorError' : ''"
                  />
                </div>
              </v-card-text>

              <v-card-actions class="justify-center">
                <v-btn
                  class="mb-3"
                  outlined
                  :disabled="!editFlag || !testimonial.temp"
                  @click="saveTestimonial"
                >
                  {{ testimonial.temp ? 'Save Testimonial' : 'Saved' }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </template>

          <div
            v-else
            class="font-italic text-center py-3"
          >
            No testimonials yet
          </div>

          <div class="d-flex justify-center">
            <v-btn
              outlined
              :disabled="!editFlag"
              @click="addTestimonial"
            >
              Add new testimonial
            </v-btn>
          </div>
        </v-form>
      </v-card-text>
    </v-card>

    <v-container>
      <v-row
        v-if="coach"
        align="center"
        justify="center"
        class="mt-3"
        style="max-width: 600px;"
      >
        <v-btn
          v-if="!editFlag"
          color="primary"
          @click="editFlag = true"
        >
          Edit
          <v-icon
            small
          >
            create
          </v-icon>
        </v-btn>
        <v-btn
          v-else
          class="mx-1"
          :loading="isSaving"
          @click="save"
        >
          Save changes
        </v-btn>

        <!-- Publish block -->
        <v-tooltip
          v-if="!profileFlag"
          top
        >
          <template #activator="{ on, attrs }">
            <span
              v-bind="attrs"
              v-on="on"
            >
              <v-btn
                class="mx-1"
                :loading="isPublishing"
                disabled
              >
                Publish
              </v-btn>
            </span>
          </template>
          <span>Can publish after profile save.</span>
        </v-tooltip>

        <v-btn
          v-else
          class="mx-1"
          :loading="isPublishing"
          :color="coach.profile.is_profile_active ? 'warning' : 'info'"
          @click="publishProfile"
        >
          {{ coach.profile.is_profile_active ? 'Unpublish' : 'Publish' }}
        </v-btn>

        <!-- View profile block -->
        <v-tooltip
          v-if="!profileFlag
            || (coach.profile.is_profile_active && !coach.profile.url_index)"
          top
        >
          <template #activator="{ on, attrs }">
            <span
              v-bind="attrs"
              v-on="on"
            >
              <v-btn
                class="mx-1"
                disabled
              >
                {{ coach.profile.is_profile_active ? 'View' : 'View Draft' }}
              </v-btn>
            </span>
          </template>
          <span v-if="coach.profile.is_profile_active">
            Please add the URL you would like your profile to be shown on.
          </span>
          <span v-else>
            Save profile first to see draft.
          </span>
        </v-tooltip>

        <v-tooltip
          v-else
          top
        >
          <template #activator="{ on, attrs }">
            <span
              v-bind="attrs"
              v-on="on"
            >
              <v-btn
                class="mx-1"
                @click="viewProfile"
              >
                {{ coach.profile.is_profile_active ? 'View' : 'View Draft' }}
              </v-btn>
            </span>
          </template>
          <span>
            Only changes that have been saved will display in profile
          </span>
        </v-tooltip>
      </v-row>
    </v-container>

    <ImageProfileUpload
      v-if="coach"
      :dialog.sync="imageDialog"
      :uuid="coach.uuid"
      :image.sync="coach.profile_image"
    />

    <!--<ChangePassword
      :dialog.sync="passwordDialog"
    />-->

    <UrlEditor
      v-if="coach && coach.profile"
      :coach="coach"
      :flag.sync="urlFlag"
      :prefix-url="prefixUrl"
      :url.sync="coach.profile.url_index"
      :original-data:.sync="originalData"
    />
  </div>
</template>

<script>
// Plugin
import { mapState, mapMutations } from 'vuex';
import { VueEditor } from 'vue2-editor';

// API
import coachApi from '@/api/coach-profile/coach';
import accountApi from '@/api/account/account';

// Mixin
import apiRules from '@/mixins/apiRules';

// Component
import Locations from './widget/Locations.vue';
import UrlEditor from './widget/UrlEditor.vue';
// import ChangePassword from './widget/ChangePassword.vue';

// Dialog modal
import ImageProfileUpload from './widget/ImageProfileUpload.vue';

export default {
  components: {
    VueEditor,
    // ChangePassword,
    Locations,
    UrlEditor,
    ImageProfileUpload,
  },

  mixins: [
    apiRules,
  ],

  data: () => ({
    valid: true,
    coach: null,
    profileFlag: false,
    rules: {
      name: [
        (v) => !!v || 'Name is required.',
      ],
      email: [
        (v) => !!v || 'E-mail is required.',
        (v) => /.+@.+\..+/.test(v) || 'E-mail must be valid',
      ],
      timezone: [
        (v) => !!v || 'Time zone is required.',
      ],
      validLink: [
        (v) => {
          if (!v) return true;
          const regex = /^http(s?):\/\/?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=]+/gm;
          return regex.test(v) || 'The link must be valid url. (ex. http(s)://example.com)';
        },
      ],
      validEmail: [
        (v) => {
          if (!v) return true;
          // eslint-disable-next-line no-useless-escape
          const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return regex.test(v) || 'The email must be valid email.';
        },
      ],
      testimonial: [
        (v) => !!v || 'Testimonial information is required.',
      ],
    },
    timezoneList: [{ display: '', value: '-' }],
    languages: [],
    experiences: [
      {
        key: 'less_than_two',
        label: 'Less than 2 years',
      },
      {
        key: 'two_to_five',
        label: '2 to 5 years',
      },
      {
        key: 'five_to_ten',
        label: '5 to 10 years',
      },
      {
        key: 'more_than_ten',
        label: 'More than 10 years',
      },
    ],
    services: [],
    testimonialPanel: [],
    isSaving: false,
    isLoading: false,
    isPublishing: false,
    accreditationsInput: '',
    editorToolbar: [
      ['bold', 'italic', 'underline'],
      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
    ],
    editFlag: false,
    imageDialog: false,
    // passwordDialog: false,
    // If url_index not changed, do not request same url name
    originalData: {
      url_index: '',
    },
    urlFlag: false,
    locations: [],
  }),

  computed: {
    ...mapState({
      user: (state) => state.account.user,
    }),

    initials() {
      if (!this.coach.first_name && !this.coach.last_name) return 'X_X';
      let init1 = '';
      let init2 = '';
      if (this.coach.first_name) {
        init1 = this.coach.first_name[0].substring(0, 1).toUpperCase();
      }
      if (this.coach.last_name) {
        init2 = this.coach.last_name[0].substring(0, 1).toUpperCase();
      }
      return `${init1} ${init2}`;
    },

    selectedTimezone: {
      // Computed needed cause Combo box have a problem connection with Object for now.
      // https://github.com/vuetifyjs/vuetify/issues/5479
      get() {
        return this.coach.time_zone ? this.coach.time_zone : '';
      },
      set(newValue) {
        if (newValue) {
          this.coach.time_zone = newValue.value;
        }
      },
    },

    prefixUrl() {
      if (this.coach && this.coach.profile && this.coach.profile.host_url) {
        return `${this.coach.profile.host_url}/`;
      }
      const envName = process.env.VUE_APP_ENV === 'prod' ? '' : `-${process.env.VUE_APP_ENV}`;
      return `profile${envName}.gcgroup.io/`;
    },
  },

  async created() {
    if (this.user.is_coach) {
      if (this.user.certifications
        .some((el) => ['indiv_coaching', 'team_coaching'].includes(el.certification.id))) {
        this.initCoach();
        this.getCoach();
        this.getLanguages();
        this.getTimezone();
      } else {
        this.$EventBus.$emit('certification-needed');
        this.$router.push({ name: 'certifications' });
      }
    } else {
      this.$toast.error('Only certified coaches can access this page.');
      this.$router.go(-1);
    }
  },

  methods: {
    ...mapMutations('account', [
      'userUpdated',
    ]),

    initCoach() {
      this.coach = {
        profile: { url_index: '' },
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    async getCoach() {
      try {
        this.isLoading = true;
        const response = await coachApi.getProfile();
        if (!response.profile) {
          response.profile = {
            url_index: '',
            testimonials: [],
          };
          this.profileFlag = false;
        } else {
          this.profileFlag = true;
        }
        if (!response.profile.company || !response.profile.company.name) {
          response.profile.company = {
            name: '',
            email: '',
            telephone: '',
            website: '',
            address: '',
          };
        }
        if (!response.services) response.services = [];
        if (response.profile && response.profile.url_index) {
          this.originalData.url_index = response.profile.url_index;
        }
        if (response.profile.locations) {
          this.locations = response.profile.locations
            .map((el) => ({ ...el, temp: false }));
        }
        if (response.profile.testimonials) {
          response.profile.testimonials = response.profile.testimonials
            .map((el) => ({ ...el, temp: false }));
        }
        this.coach = response;
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },

    async getLanguages() {
      try {
        this.languages = await coachApi.getLanguages();
      } catch (err) {
        console.log(err);
        this.$toast.error(err.msg);
      }
    },

    async getTimezone() {
      try {
        const response = await accountApi.getTimezone();
        if (response) {
          this.timezoneList = response;
        }
      } catch (err) {
        console.log(err);
        this.$toast.error(err.msg);
      }
    },

    addTestimonial() {
      let list = [];
      if (this.coach && this.coach.profile && this.coach.profile.testimonials) {
        list = [...this.coach.profile.testimonials];
      }
      list.push({
        author: '',
        position: '',
        company: '',
        content: '',
        temp: true,
      });

      this.coach.profile.testimonials = list;
    },

    removeTestimonial(index) {
      this.coach.profile.testimonials.splice(index, 1);
      this.testimonialPanel = [];
    },

    addAccreditation() {
      if (this.accreditationsInput !== '') {
        if (!this.coach.profile.accreditations) this.coach.profile.accreditations = [];
        this.coach.profile.accreditations.push(this.accreditationsInput);
        this.accreditationsInput = '';
      }
    },

    removeAccreditation(key) {
      this.coach.profile.accreditations.splice(key, 1);
    },

    async save() {
      let testimonialFlag = true;
      if (this.coach.profile.testimonials && this.coach.profile.testimonials.length) {
        this.coach.profile.testimonials = this.coach.profile.testimonials.map((el) => {
          const unit = el;
          if (!unit.content) {
            testimonialFlag = false;
            unit.flag = true;
          } else if (unit.content && unit.flag) {
            delete unit.flag;
          }
          return unit;
        });
      }
      if (this.$refs.form.validate() && testimonialFlag) {
        this.isSaving = true;
        try {
          // let sendData = { ...this.coach };
          let sendData = JSON.parse(JSON.stringify(this.coach));
          if (this.locations && this.locations.length) {
            const filteredList = [];
            this.locations.forEach((location) => {
              if (location.country || location.region || location.city) {
                const unit = {};
                Object.keys(location).forEach((key) => {
                  if (key !== 'temp' && location[key]) {
                    unit[key] = location[key].id;
                  }
                });
                filteredList.push(unit);
              }
            });
            sendData.profile.locations = filteredList;
          }
          delete sendData.profile_image;
          delete sendData.profile.url_index;
          if (sendData.profile.company) {
            Object.keys(sendData.profile.company).forEach((key) => {
              if (!sendData.profile.company[key]) {
                sendData.profile.company[key] = null;
              }
            });
          }
          if (sendData.profile.testimonials) {
            const filteredList = [];
            sendData.profile.testimonials.forEach((testimonial) => {
              if (testimonial.author && testimonial.content) {
                const unit = {};
                Object.keys(testimonial).forEach((key) => {
                  if (key !== 'temp' && testimonial[key]) {
                    unit[key] = testimonial[key];
                  }
                });
                filteredList.push(unit);
              }
            });
            sendData.profile.testimonials = filteredList;
          }
          const response = await coachApi.patchProfile(sendData);
          this.userUpdated(response);
          this.$toast.success('Changes successfully saved.');
          if (response.profile) {
            this.coach.profile = response.profile;
            this.profileFlag = true;
            if (response.profile.url_index) {
              this.originalData.url_index = response.profile.url_index;
            }
          }
          sendData = null;
          this.editFlag = false;
        } catch (error) {
          console.log(error);
          if (error.field) {
            this.$toast.error('Please check if all fields are valid');
            this.apiRules = error.field;
            this.$vuetify.goTo(0, {
              duration: 700,
              offset: 0,
              easing: 'easeInOutCubic',
            });
          } else {
            this.$toast.error(error.msg);
          }
        } finally {
          this.isSaving = false;
        }
      } else {
        if (!testimonialFlag) {
          this.$toast.error('Fill out Testimonial fields');
        } else {
          this.$toast.error('Fill out required fields');
        }
        // Scroll to top
        this.$vuetify.goTo(0, {
          duration: 700,
          offset: 0,
          easing: 'easeInOutCubic',
        });
      }
    },

    async locationSave() {
      try {
        this.isSaving = true;
        let sendData = { profile: { locations: null } };
        if (this.locations && this.locations.length) {
          const filteredList = [];
          this.locations.forEach((location) => {
            if (location.country || location.region || location.city) {
              const unit = {};
              Object.keys(location).forEach((key) => {
                if (key !== 'temp' && location[key]) {
                  unit[key] = location[key].id;
                }
              });
              filteredList.push(unit);
            }
          });
          sendData.profile.locations = filteredList;
        }
        const response = await coachApi.patchProfile(sendData);
        this.userUpdated(response);
        this.$toast.success('Location successfully saved.');
        if (response.profile.locations) {
          this.locations = response.profile.locations.map((el) => ({ ...el, temp: false }));
        }
        sendData = null;
      } catch (error) {
        console.log(error);
        if (error.field) {
          this.$toast.error('Please check if all fields are valid');
          this.apiRules = error.field;
          this.$vuetify.goTo(0, {
            duration: 700,
            offset: 0,
            easing: 'easeInOutCubic',
          });
        } else {
          this.$toast.error(error.msg);
        }
      } finally {
        this.isSaving = false;
      }
    },

    async saveTestimonial() {
      let testimonialFlag = true;
      if (this.coach.profile.testimonials && this.coach.profile.testimonials.length) {
        this.coach.profile.testimonials = this.coach.profile.testimonials.map((el) => {
          const unit = el;
          if (!unit.content) {
            testimonialFlag = false;
            unit.flag = true;
          } else if (unit.content && unit.flag) {
            delete unit.flag;
          }
          return unit;
        });
      }
      if (testimonialFlag) {
        this.isSaving = true;
        try {
          let sendData = { profile: { testimonials: this.coach.profile.testimonials } };
          if (sendData.profile.testimonials) {
            const filteredList = [];
            sendData.profile.testimonials.forEach((testimonial) => {
              if (testimonial.author && testimonial.content) {
                const unit = {};
                Object.keys(testimonial).forEach((key) => {
                  if (key !== 'temp' && testimonial[key]) {
                    unit[key] = testimonial[key];
                  }
                });
                filteredList.push(unit);
              }
            });
            sendData.profile.testimonials = filteredList;
          }
          const response = await coachApi.patchProfile(sendData);
          this.userUpdated(response);
          this.$toast.success('Changes successfully saved.');
          if (response.profile.testimonials) {
            this.coach.profile.testimonials = response.profile.testimonials
              .map((el) => ({ ...el, temp: false }));
          }
          sendData = null;
        } catch (error) {
          console.log(error);
          if (error.field) {
            this.apiRules = error.field;
            this.$vuetify.goTo(0, {
              duration: 700,
              offset: 0,
              easing: 'easeInOutCubic',
            });
            // this.$toast.error('Please check if all fields are valid');
            if (!this.isErrorMatchingField(this.coach)) {
              this.$toast.error(this.apiErrorMsg);
            }
          } else {
            this.$toast.error(error.msg);
          }
        } finally {
          this.isSaving = false;
        }
      } else {
        this.$toast.error('Fill out Testimonial fields');
      }
    },

    async publishProfile() {
      try {
        this.isPublishing = true;
        const response = await coachApi.publishProfile(this.coach.uuid);
        if (response) {
          this.coach.profile.is_profile_active = response.profile.is_profile_active;
          this.coach.profile.url_index = response.profile.url_index;
          this.originalData.url_index = response.profile.url_index;
          this.$toast.success(response.profile.is_profile_active
            ? 'The profile successfully published.'
            : 'The profile successfully unpublished.');
        }
      } catch (err) {
        console.log(err);
        this.$toast.error(err.msg);
      } finally {
        this.isPublishing = false;
      }
    },

    viewProfile() {
      let url = '';
      if (this.coach.profile.is_profile_active) {
        url = `${this.coach.profile.host_url}/${this.coach.profile.url_index}`;
      } else {
        url = `${this.coach.profile.host_url}/coach/draft/`;
      }
      window.open(url, '_blank');
    },

    copyLinkToClipboard(option) {
      let url = '';
      if (option === 'profile') {
        const envName = process.env.VUE_APP_ENV === 'prod' ? '' : `-${process.env.VUE_APP_ENV}`;
        url = `${this.coach.profile.host_url || `profile${envName}.gcgroup.io/`}/${this.coach.profile.url_index}`;
      } else if (option === 'linkedin') {
        url = this.coach.profile.linkedin_url;
      }

      try {
        this.$copyText(url);
        this.$toast.success('Copied!');
      } catch (err) {
        this.$toast.error('Can not copy');
        console.log(err);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .EditorError {
    background-color: rgba(255, 0, 0, 0.2);
  }
</style>

<style lang="scss">
  .accreditationChip {
    &.v-chip {
      word-break: break-word;
      white-space: normal;
      height: 100%;
      padding: 4px 12px;
      margin: 5px 4px !important;
    }
  }
</style>
